import React, { useContext } from 'react';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { useLocale } from 'locale';
import PlaceholderIcon from 'assets/svg/developer/project-placeholder.svg';
import { Body as ListingCardBody } from 'ds/components/ListingCard';
import { LineLabels, MarketplaceTypeLabel } from 'components/cards/listing/UniversalCard/styled';
import { DeveloperProjectMarketplace, IDeveloperProject, ProjectIntegrationLevel } from 'utils/entities';
import { useImageUrlBuilder, UserAgentContext } from 'hooks/useImageUrlBuilder';
import * as Styled from './styled';
import { isIOS } from 'utils/uaMatchers';
import { developerNameSelector } from 'screens/DeveloperPage/selectors';
import DiscountLabel from 'components/cards/listing/UniversalCard/DiscountLabel';


const IMAGE_PIXEL_DENSITY = 2;

interface CardInnerProps {
  project: IDeveloperProject;
  developerName: string;
  isMapPreview?: boolean;
  headingTagH3?: boolean;
}

const CardInner: React.FC<CardInnerProps> = ({ project, developerName, isMapPreview, headingTagH3 }) => {
  const { projectName, marketplaceType, integrationLevel, targetingDisplay, images } = project;
  const marketingText = targetingDisplay && targetingDisplay.subTitle;
  const imageUrl = Array.isArray(images) && images.length ? images.find(img => img.isMain).url : null;
  const { t } = useLocale();
  const constructImageURI = useImageUrlBuilder();
  const userAgent = useContext(UserAgentContext);
  const isIOSDevice = isIOS(userAgent);
  const name = projectName ? projectName : t('universalCard.projectLabel.defaultName');
  const projectAddress = project ? `${project.address.street || ''} ${project.address.houseNumber || ''} ${project.address.settlement || ''}`.trim() : '';
  const isFullIntegration = integrationLevel === ProjectIntegrationLevel.Full;
  const isDevIntegration = integrationLevel === ProjectIntegrationLevel.DevDisplay;
  const projectImageSrc = (isFullIntegration || isDevIntegration) && imageUrl
    ? constructImageURI(imageUrl, { height: Styled.IMAGE_HEIGHT * IMAGE_PIXEL_DENSITY, fit: 'contain' })
    : null;
  const isCommercial = marketplaceType === DeveloperProjectMarketplace.Commercial;
  const SecondLineLabel = isMapPreview ? Styled.MapPreviewSecondLineLabel : Styled.SecondLineLabel;
  const Body = isMapPreview ? Styled.MapPreviewBody : ListingCardBody;
  const bodyProps = isMapPreview ? { isMapPreview, isIOS: isIOSDevice } : null;

  const FirstLineLabel = headingTagH3 ? Styled.FirstLineLabelH3 : Styled.FirstLineLabel;

  return (
    <>
      <Styled.ProjectImageWrapper isMapPreview={isMapPreview}>
        {projectImageSrc
          ? (
            <Styled.ProjectImage isMapPreview={isMapPreview}>
              <img
                src={projectImageSrc}
                loading="lazy"
                alt={`${projectAddress ? projectAddress + ' - ' : ''}${developerName}`}
              />
            </Styled.ProjectImage>
          ) : <PlaceholderIcon />
        }
        {project.discount && project.discount.showDiscount ? <DiscountLabel /> : null}
      </Styled.ProjectImageWrapper>
      <Body {...bodyProps}>
        <Styled.MarketplaceTypeLabelWrapper>
          <MarketplaceTypeLabel isCommercial={isCommercial}>
            {t('listingCard.marketplaceTypeLabel', { isCommercial })}
          </MarketplaceTypeLabel>
        </Styled.MarketplaceTypeLabelWrapper>
        <LineLabels data-auto="property-details">
          <FirstLineLabel weight={isMapPreview ? 'medium' : 'bold'}>
            {t('universalCard.project.name', { projectName: name })}
          </FirstLineLabel>
          {(isFullIntegration || isDevIntegration) && marketingText ? (
            <SecondLineLabel>
              {marketingText}
            </SecondLineLabel>
          ) : null}
        </LineLabels>
      </Body>
    </>
  );
};

const mapStateToProps = (state: State) => ({
  developerName: developerNameSelector(state),
});

export default connect(mapStateToProps)(CardInner);
